import React, { useCallback } from 'react';
import DeviceCard from './DeviceCard';

// Determine which display group a device belongs to
function getDeviceGroup(device) {
  const { platform, mode } = device;
  if (['netatmo', 'airthings'].includes(platform)) return 'sensors';
  if (['sensibo', 'daikin', 'goe', 'ebeco', 'danfoss'].includes(platform)) return 'controllers';
  if (platform === 'shelly') {
    return mode === 'sensor' ? 'sensors' : 'controllers';
  }
  return 'other';
}

const GROUP_ORDER = ['sensors', 'controllers', 'other'];
const GROUP_LABELS = {
  sensors:     'Sensors',
  controllers: 'Controllers',
  other:       'Other',
};

// Sensor platform priority: Airthings=0, Netatmo=1, Shelly CO2=2, Shelly Temp/Hum=3, other=4
function getSensorPlatformOrder(device) {
  if (device.platform === 'airthings') return 0;
  if (device.platform === 'netatmo') return 1;
  if (device.platform === 'shelly' && device.mode === 'sensor') {
    const params = device.monitored_parameters || [];
    return params.includes('co2') ? 2 : 3;
  }
  return 4;
}

function sortWithinGroup(devices, activeAlerts, group) {
  return [...devices].sort((a, b) => {
    // Offline first
    const aOff = a.error === true ? 1 : 0;
    const bOff = b.error === true ? 1 : 0;
    if (bOff !== aOff) return bOff - aOff;
    // For sensors: sort by platform order before alerts
    if (group === 'sensors') {
      const aPlatformOrder = getSensorPlatformOrder(a);
      const bPlatformOrder = getSensorPlatformOrder(b);
      if (aPlatformOrder !== bPlatformOrder) return aPlatformOrder - bPlatformOrder;
    }
    // Then most alerts
    const aId = a.device_id || a.id;
    const bId = b.device_id || b.id;
    const aAlerts = activeAlerts.filter(al => al.device_id === aId).length;
    const bAlerts = activeAlerts.filter(al => al.device_id === bId).length;
    if (bAlerts !== aAlerts) return bAlerts - aAlerts;
    // Then alphabetically
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * DevicesDashboard - Grouped grid of device cards
 *
 * Props:
 *   - devices: array of device objects from DeviceData
 *   - activeAlerts: array of active alert objects
 *   - configs: MonitoringControl_configs object
 *   - onSaveConfig: function(updatedConfigs)
 *   - onSaveDeviceNotes: function(deviceId, notes) - saves notes to DeviceData in DynamoDB
 *   - isLoading: boolean
 */
const DevicesDashboard = ({ devices, activeAlerts, configs, onSaveConfig, onSaveDeviceNotes, isLoading }) => {
  const systemAlertConfigs = configs?.system_alerts || {};
  const deviceOverrides = configs?.device_overrides || {};
  const allRules = configs?.application_alerts?.rules || [];

  // Save a per-device monitoring override into MonitoringControl_configs
  const handleSaveOverride = useCallback((deviceId, changes) => {
    const updatedConfigs = {
      ...configs,
      device_overrides: {
        ...deviceOverrides,
        [deviceId]: {
          ...deviceOverrides[deviceId],
          ...changes
        }
      }
    };
    if (onSaveConfig) onSaveConfig(updatedConfigs);
  }, [configs, deviceOverrides, onSaveConfig]);

  const handleSaveRules = useCallback((updatedRules) => {
    const updatedConfigs = {
      ...configs,
      application_alerts: {
        ...configs?.application_alerts,
        rules: updatedRules
      }
    };
    if (onSaveConfig) onSaveConfig(updatedConfigs);
  }, [configs, onSaveConfig]);

  if (isLoading) {
    return <div className="alerts-loading">Loading devices...</div>;
  }

  if (!devices || devices.length === 0) {
    return (
      <div className="alerts-empty">
        <div className="alerts-empty-icon">—</div>
        <p>No monitored devices</p>
        <p className="alerts-empty-subtitle">Devices will appear here once monitoring is configured.</p>
      </div>
    );
  }

  // Group devices
  const groups = {};
  for (const device of devices) {
    const group = getDeviceGroup(device);
    if (!groups[group]) groups[group] = [];
    groups[group].push(device);
  }

  // Summary counts
  const offlineCount = devices.filter(d => d.error === true).length;
  const alertedCount = new Set(activeAlerts.map(a => a.device_id)).size;

  return (
    <div className="devices-dashboard">
      {/* Summary bar */}
      <div className="devices-summary">
        <span className="summary-item">
          <span className="summary-count">{devices.length}</span> devices
        </span>
        {offlineCount > 0 && (
          <span className="summary-item summary-offline">
            <span className="summary-count">{offlineCount}</span> offline
          </span>
        )}
        {alertedCount > 0 && (
          <span className="summary-item summary-alerted">
            <span className="summary-count">{alertedCount}</span> with alerts
          </span>
        )}
      </div>

      {/* Grouped device grids */}
      {GROUP_ORDER.filter(g => groups[g]?.length > 0).map(group => (
        <div key={group} className="device-group">
          <h4 className="device-group-title">{GROUP_LABELS[group]}</h4>
          <div className="devices-grid">
            {sortWithinGroup(groups[group], activeAlerts, group).map(device => {
              const deviceId = device.device_id || device.id;
              const deviceAlerts = activeAlerts.filter(
                a => a.device_id === deviceId || a.device_id === device.id
              );
              return (
                <DeviceCard
                  key={deviceId}
                  device={device}
                  activeAlerts={deviceAlerts}
                  notes={device.notes || ''}
                  onSaveNotes={onSaveDeviceNotes}
                  deviceOverride={deviceOverrides[deviceId] || {}}
                  onSaveOverride={handleSaveOverride}
                  systemAlertConfigs={systemAlertConfigs}
                  allRules={allRules}
                  allDevices={devices}
                  onSaveRules={handleSaveRules}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DevicesDashboard;
